import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'
import { saveWithConflictCheck } from '../../../lib/conflictResolver'

const EVENT_FIELDS = `
  id, calendar_id, title, description, location,
  start_time, end_time, is_all_day, recurrence_rule, recurrence_end,
  created_by, driver_id, updated_at, event_color,
  event_members(user_id),
  calendars(id, name, color)
`

export const useCalendarEvents = (rangeStart, rangeEnd) => {
  const { household } = useAuth()
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: ['events', household?.id, rangeStart, rangeEnd],
    staleTime: 0,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      if (!household?.id) return { nonRecurring: [], recurring: [] }

      // ── Own household events ──────────────────────────────────────────────
      const [{ data: nonRecurring, error: e1 }, { data: recurring, error: e2 }] = await Promise.all([
        supabase
          .from('events')
          .select(EVENT_FIELDS)
          .eq('household_id', household.id)
          .is('recurrence_rule', null)
          .gte('start_time', rangeStart)
          .lte('start_time', rangeEnd),
        supabase
          .from('events')
          .select(EVENT_FIELDS)
          .eq('household_id', household.id)
          .not('recurrence_rule', 'is', null)
          .or(`recurrence_end.is.null,recurrence_end.gte.${rangeStart}`),
      ])
      if (e1) throw e1
      if (e2) throw e2

      // ── Shared calendar events ────────────────────────────────────────────
      const { data: sharedCalRecords } = await supabase
        .from('shared_calendars')
        .select('calendar_id')
        .eq('shared_to_household_id', household.id)

      const sharedCalIds = (sharedCalRecords ?? []).map((r) => r.calendar_id)

      let sharedNonRecurring = []
      let sharedRecurring    = []

      if (sharedCalIds.length > 0) {
        const [{ data: snr }, { data: sr }] = await Promise.all([
          supabase
            .from('events')
            .select(EVENT_FIELDS)
            .in('calendar_id', sharedCalIds)
            .is('recurrence_rule', null)
            .gte('start_time', rangeStart)
            .lte('start_time', rangeEnd),
          supabase
            .from('events')
            .select(EVENT_FIELDS)
            .in('calendar_id', sharedCalIds)
            .not('recurrence_rule', 'is', null)
            .or(`recurrence_end.is.null,recurrence_end.gte.${rangeStart}`),
        ])
        sharedNonRecurring = (snr ?? []).map((e) => ({ ...e, isReadOnly: true }))
        sharedRecurring    = (sr  ?? []).map((e) => ({ ...e, isReadOnly: true }))
      }

      return {
        nonRecurring: [...(nonRecurring ?? []), ...sharedNonRecurring],
        recurring:    [...(recurring    ?? []), ...sharedRecurring],
      }
    },
    enabled: !!household?.id && !!rangeStart && !!rangeEnd,
  })

  useEffect(() => {
    if (!household?.id) return

    const channel = supabase
      .channel(`events-${household.id}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'events', filter: `household_id=eq.${household.id}` },
        () => qc.invalidateQueries({ queryKey: ['events', household.id] }),
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'event_members' },
        () => qc.invalidateQueries({ queryKey: ['events', household.id] }),
      )
      .subscribe()

    return () => { supabase.removeChannel(channel) }
  }, [household?.id, qc])

  return query
}

export const useEventMutations = () => {
  const { household, user } = useAuth()
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: ['events', household?.id] })

  const createEvent = async ({ memberIds = [], ...fields }) => {
    const { data: event, error } = await supabase
      .from('events')
      .insert({ ...fields, household_id: household.id, created_by: user.id })
      .select()
      .single()
    if (error) throw error

    if (memberIds.length > 0) {
      const { error: me } = await supabase
        .from('event_members')
        .insert(memberIds.map((uid) => ({ event_id: event.id, user_id: uid })))
      if (me) throw me
    }
    invalidate()
    return event
  }

  const updateEvent = async ({ id, memberIds, localUpdatedAt, ...fields }) => {
    if (localUpdatedAt) {
      const result = await saveWithConflictCheck('events', id, fields, localUpdatedAt)
      if (result.conflict) return result
    } else {
      const { error } = await supabase.from('events').update(fields).eq('id', id)
      if (error) throw error
    }

    if (memberIds !== undefined) {
      await supabase.from('event_members').delete().eq('event_id', id)
      if (memberIds.length > 0) {
        await supabase
          .from('event_members')
          .insert(memberIds.map((uid) => ({ event_id: id, user_id: uid })))
      }
    }
    invalidate()
    return {}
  }

  const deleteEvent = async (id) => {
    const { error } = await supabase.from('events').delete().eq('id', id)
    if (error) throw error
    invalidate()
  }

  return { createEvent, updateEvent, deleteEvent }
}
